import { createContext, useContext } from "react";
import { ProjectCharge } from "~/gql/types";

/**
 * The durable seam between a host page and the charge editor. The host owns a
 * set of charges (derived from its own context — for the project-charges page,
 * the charges matching the current URL filters), resolves a charge by id out of
 * that set, dismisses the editor, and absorbs a saved charge back into its set.
 *
 * The editor is presentational: it never fetches or picks its own charge. Any
 * host (the project-charges page now, the invoice-details page later) can
 * satisfy this shape.
 */
export type ChargeEditorHost = {
  status: "loading" | "ready";
  resolve: (id: number) => ProjectCharge | undefined;
  dismiss: () => void;
  absorbSaved: (id: number) => void;
};

export const ChargeEditorHostContext = createContext<ChargeEditorHost | null>(
  null
);

export function useChargeEditorHost(): ChargeEditorHost {
  const context = useContext(ChargeEditorHostContext);

  if (!context) {
    throw new Error(
      "Cannot call useChargeEditorHost outside of a ChargeEditorHostContext.Provider"
    );
  }

  return context;
}
